import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { Section } from "@/components/Section";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { FaqAccordion, type FaqItem } from "@/components/FaqAccordion";
import { buildSeo } from "@/lib/seo";
import type { ActionPlan } from "@/lib/action-plan";
import { ActionPlanForm, ActionPlanView } from "@/components/action-plan/ActionPlanTool";

export const Route = createFileRoute("/ai-action-plan")({
  head: () =>
    buildSeo({
      title: "How to Use AI in Marketing: Free Action Plan | Freelancer Tamal",
      description:
        "Learn how to use AI in marketing with a free SEO, AEO and GEO action plan. Turn your website content and business goals into priorities, plus planning FAQs.",
      path: "/ai-action-plan",
      alternates: { bn: "/bn/ai-action-plan" },
      ogEyebrow: "AI Action Plan",
      keywords: "how to use AI in marketing, AI marketing strategy, AI marketing strategy planning, AI marketing plan generator, AI SEO plan, AEO action plan, GEO strategy",
    }),
  component: ActionPlanPage,
});


const PLANNING_FAQS: FaqItem[] = [
  {
    q: "How do I use AI in marketing?",
    a: "Start with one marketing goal and a specific task: grouping customer questions, drafting a content outline, or prioritizing website improvements. Give AI your audience, offer, market and existing copy, then review its suggestions against what your customers actually need. For search marketing, use the plan here as a starting point for SEO (search engine optimization), AEO (answer engine optimization) and GEO (generative engine optimization), not as a substitute for judgment.",
  },
  {
    q: "How do I create an AI marketing strategy for my business?",
    a: "Define the business outcome first: more qualified enquiries, sales, or repeat customers. Identify your audience, record your current results and choose a channel you can maintain. Ask AI to propose actions with reasons, effort and priorities using your real business context. Review the recommendations, assign an owner and a budget, and measure progress. This planner covers search visibility; it does not produce a complete paid-ad, email or social-media strategy.",
  },
  {
    q: "Which AI marketing tools should a small business use first?",
    a: "Choose a tool for a task you already do, rather than buying a large stack. A writing assistant can help with outlines and first drafts; a planning tool can organize website improvements. Compare the cost, privacy terms, review effort and compatibility with your existing workflow. Keep tools that save time without reducing accuracy. AI suggestions still need checks against real search data and customer feedback.",
  },
  {
    q: "How can I adapt my marketing strategy to AI search?",
    a: "Keep useful, accessible pages and traditional SEO as your foundation. Answer specific customer questions clearly, describe your services consistently and support claims with evidence visitors can inspect. Add relevant structured data only when it matches visible content. AI can help identify missing answers and organize improvements, but it cannot guarantee a citation in ChatGPT, Gemini or Google AI Overviews. Treat AI visibility as another channel, not a reason to abandon search or referrals.",
  },
  {
    q: "How is AI search changing B2B marketing strategy?",
    a: "AI answers give buyers another way to research suppliers and compare options. A practical B2B response is to make service details, use cases, limitations and case studies easy to find and understand. Use real buyer questions to guide content rather than publishing generic AI-written articles at scale. Track qualified enquiries and ask prospects how they found you; a mention in an AI answer alone does not prove that it generated a sale.",
  },
  {
    q: "How do I measure whether an AI marketing plan is working?",
    a: "Record a baseline before making changes, then track the outcome tied to your goal: qualified leads, organic enquiries or sales. Use Search Console for search impressions and clicks, and analytics for visits and conversions. Log what changed and compare equivalent periods while accounting for seasonality. Track time saved separately from business results. Rankings, AI citations and generated content are supporting signals, not proof of revenue or a guaranteed return.",
  },
  {
    q: "What should I include in an AI marketing planning prompt?",
    a: "Include your business type, target market, offer, audience questions, goals and the page text you want reviewed. Describe constraints such as available time or budget when using a tool that accepts them. Ask for a prioritized list with reasons and concrete next steps. Paste only content you have permission to share; leave out passwords, confidential client material and personal customer data. This planner bases its recommendations on the text you paste; the optional website address does not trigger a live site audit.",
  },
];

function ActionPlanPage() {
  const [plan, setPlan] = useState<ActionPlan | null>(null);

  return (
    <>
      <Breadcrumbs items={[{ name: "Home", path: "/" }, { name: "AI Action Plan", path: "/ai-action-plan" }]} />
      <Section
        headingLevel={1}
        eyebrow="Free AI Action Plan"
        title={<>How to use <span className="text-gradient">AI in marketing.</span></>}
        className="!pt-12 !pb-12 md:!pt-20 md:!pb-16"
      >
        <p className="max-w-2xl text-lg text-muted-foreground -mt-8 mb-10">
          Turn your business goals and website copy into a prioritized SEO, AEO and GEO action plan for
          search and AI-answer visibility. Review the recommendations before putting them into practice.
        </p>

        <ActionPlanForm lang="en" onPlan={setPlan} />
      </Section>

      {plan && <ActionPlanView plan={plan} lang="en" />}

      <Section eyebrow="Planning FAQ" title="AI marketing strategy questions." className="!pt-8 md:!pt-12">
        <div className="max-w-3xl">
          <FaqAccordion items={PLANNING_FAQS} idPrefix="ai-marketing-faq" />
          <div className="mt-8 flex flex-wrap gap-x-6 gap-y-3 text-sm">
            <Link to="/online-marketing-small-businesses" className="text-primary underline underline-offset-4">Small-business marketing guide</Link>
            <Link to="/ai-answers" className="text-primary underline underline-offset-4">AI search questions</Link>
            <Link to="/case-studies" className="text-primary underline underline-offset-4">Real project case studies</Link>
          </div>
        </div>
      </Section>
    </>
  );
}

